import { defineStore, acceptHMRUpdate } from 'pinia';
import {
    getSessionReplayEntities,
    getStarReplay,
    createSessionReplayEntity,
    starReplay,
    unstarReplay,
    updateReplayEntity,
    getSavedClips,
    getSavedClipContext,
    watchClip,
    deleteClip,
    updateSavedClipDescription,
} from '@/vuex/modules/session-replay/session-replay.utils';
import { getEntityKey, getReplaySettings, runReplayRecordingAggregation } from '@/session-replay/helpers/common.util';
import { canViewSessionReplayForApp } from '@/vuex/modules/auth/auth.helpers';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';

interface SessionReplayEntity {
    id?: string;
    starred?: boolean;
    watched?: boolean;
    recordingSessionId?: string;
    [key: string]: unknown;
}

interface SavedClip {
    id: string;
    [key: string]: unknown;
}

interface ReplaySettings {
    [key: string]: unknown;
}

interface App {
    id: string;
    displayName?: string;
    [key: string]: unknown;
}

interface SessionReplayState {
    error: unknown;
    fetching: boolean;
    sessionReplayEntitiesById: Record<string, SessionReplayEntity>;
    savedClips: Record<string, SavedClip>;
    appReplaySettings: Record<string, ReplaySettings>;
}

let loadAllPromise: Promise<unknown> | null = null;

export function __resetLoadAllPromiseForUnitTests (): void {
    loadAllPromise = null;
}

export function getInitialState (): SessionReplayState {
    return {
        error: null,
        fetching: false,
        sessionReplayEntitiesById: {},
        savedClips: {},
        appReplaySettings: {},
    };
}

export const useSessionReplayStore = defineStore('sessionReplay', {
    state: getInitialState,
    getters: {
        sessionReplayEntitiesMap: (state) => state.sessionReplayEntitiesById,
        sessionReplayEntities: (state) => values(state.sessionReplayEntitiesById),
        getReplayEntityAtKey: (state) => (key: string): SessionReplayEntity | undefined => state.sessionReplayEntitiesById[key],
        hasReplayEntities: (state) => !isEmpty(state.sessionReplayEntitiesById),
        getClipById: (state) => (clipId: string): SavedClip | undefined => state.savedClips[clipId],
        savedClipsList: (state) => values(state.savedClips),
        liveReplayEnabledAppIds: (state) => Object.keys(state.appReplaySettings)
            .filter((appId) => get(state.appReplaySettings[appId], 'featureFlags.liveReplays', false)),
        // The liveReplays flag alone is not enough to join a session: the app must still have
        // session replay activated and exist in the apps store. Anything gating live replay UI
        // reads this so the nav cannot advertise apps a liveness check would never find.
        liveReplayApps (): App[] {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const appsStore = useAppsStore();

            return this.liveReplayEnabledAppIds
                .filter((appId: string) => appsStore.getApplicationFlag(appId, 'enableSessionReplay'))
                .map((appId: string) => appsStore.getAppById(appId))
                .filter(Boolean);
        },
    },
    actions: {
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}): Promise<void> {
            if (loadAllPromise && !reFetch) {
                await loadAllPromise;

                return;
            }

            if (this.hasReplayEntities && !reFetch) {
                return;
            }

            this.fetching = true;
            loadAllPromise = getSessionReplayEntities();

            try {
                const replays = await loadAllPromise;

                this.sessionReplayEntitiesById = replays as Record<string, SessionReplayEntity>;
            } catch (error) {
                loadAllPromise = null;
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        updateEntityAtKey ({ key, value }: { key: string; value: SessionReplayEntity }): void {
            this.sessionReplayEntitiesById[key] = value;
        },
        async createSessionReplayEntity ({ replayBody }: { replayBody: Record<string, unknown> }): Promise<void> {
            this.fetching = true;
            let body = replayBody;

            if (!body.recordingIds) {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const replayRetentionDays = useAppsStore().getAppById(body.appId)?.recordingRetentionDays;
                body = await runReplayRecordingAggregation(body, replayRetentionDays);
            }

            try {
                const value = await createSessionReplayEntity(body);

                this.sessionReplayEntitiesById[getEntityKey(body)] = value as SessionReplayEntity;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        async watchedReplay ({ replayBody }: { replayBody: Record<string, unknown> }): Promise<Record<string, unknown>> {
            this.fetching = true;
            let body = replayBody;

            if (!body.recordingIds) {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const replayRetentionDays = useAppsStore().getAppById(body.appId)?.recordingRetentionDays;
                body = await runReplayRecordingAggregation(body, replayRetentionDays);
            }

            let updatedReplay: Record<string, unknown> = body;

            await updateReplayEntity(body)
                .then((res) => {
                    this.fetching = false;
                    this.sessionReplayEntitiesById[getEntityKey(body)] = res as SessionReplayEntity;

                    if (body.recordingSessionId) {
                        this.sessionReplayEntitiesById[body.recordingSessionId as string] = res as SessionReplayEntity;
                    }

                    updatedReplay = res as Record<string, unknown>;
                })
                .catch((error) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });

            return updatedReplay;
        },
        async starReplay ({ replayBody }: { replayBody: Record<string, unknown> }): Promise<void> {
            this.fetching = true;
            let replayEntity = this.getReplayEntityAtKey(replayBody?.recordingSessionId as string);

            if (!replayEntity) {
                await this.createSessionReplayEntity({ replayBody });
                replayEntity = this.getReplayEntityAtKey(replayBody?.recordingSessionId as string);
            }

            try {
                await starReplay(replayEntity!.id!);

                const entity = this.sessionReplayEntitiesById[getEntityKey(replayBody)];

                if (entity) {
                    entity.starred = true;
                }
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        async unstarReplay ({ replay }: { replay: SessionReplayEntity }): Promise<void> {
            const replayId = replay.id!;

            this.fetching = true;

            await unstarReplay(replayId)
                .then(() => {
                    this.fetching = false;

                    const entity = this.sessionReplayEntitiesById[getEntityKey(replay)];

                    if (entity) {
                        entity.starred = false;
                    }
                })
                .catch((error) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async getStarReplay ({ replayId }: { replayId: string }): Promise<unknown> {
            this.fetching = true;

            return getStarReplay(replayId)
                .then((starredStatus) => {
                    this.fetching = false;

                    return starredStatus;
                })
                .catch((error) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async loadSavedClips ({ reFetch = false, createdByMe = true }: { reFetch?: boolean; createdByMe?: boolean } = {}): Promise<void> {
            if (!reFetch && !isEmpty(this.savedClipsList)) {
                return;
            }

            this.fetching = true;

            await getSavedClips(createdByMe)
                .then((clips) => {
                    this.savedClips = keyBy(clips as SavedClip[], 'id');
                })
                .catch((error) => {
                    this.error = error;
                    throw error;
                })
                .finally(() => {
                    this.fetching = false;
                });
        },
        async loadSavedClip ({ id, reFetch = false, abortSignal }: { id: string; reFetch?: boolean; abortSignal?: AbortSignal }): Promise<void> {
            if (!reFetch && this.getClipById(id)) {
                return;
            }

            this.fetching = true;

            await getSavedClipContext(id, abortSignal)
                .then((clip) => {
                    this.savedClips[id] = clip as SavedClip;
                })
                .catch((error) => {
                    this.error = error;
                    throw error;
                })
                .finally(() => {
                    this.fetching = false;
                });
        },
        async watchSavedClip ({ id }: { id: string }): Promise<void> {
            this.fetching = true;

            await watchClip(id)
                .then((clip) => {
                    this.savedClips[id] = clip as SavedClip;
                })
                .catch((error) => {
                    this.error = error;
                    throw error;
                })
                .finally(() => {
                    this.fetching = false;
                });
        },
        async deleteSavedClip ({ id }: { id: string }): Promise<void> {
            this.fetching = true;

            await deleteClip(id)
                .then(() => {
                    delete this.savedClips[id];
                })
                .catch((error) => {
                    this.error = error;
                    throw error;
                })
                .finally(() => {
                    this.fetching = false;
                });
        },
        async updateSavedClipDescription ({ id, description }: { id: string; description: string }): Promise<void> {
            this.fetching = true;

            await updateSavedClipDescription({ clipId: id, description })
                .then((clip) => {
                    this.savedClips[id] = clip as SavedClip;
                })
                .catch((error) => {
                    this.error = error;
                    throw error;
                })
                .finally(() => {
                    this.fetching = false;
                });
        },
        async loadReplaySettingsForApp ({ appId, reFetch }: { appId: string; reFetch?: boolean }): Promise<ReplaySettings> {
            if (reFetch || !this.appReplaySettings[appId]) {
                const replaySettings = await getReplaySettings({ appId });

                this.appReplaySettings[appId] = replaySettings as ReplaySettings;
            }

            return this.appReplaySettings[appId];
        },
        setReplaySettingsForApp ({ appId, replaySettings }: { appId: string; replaySettings: ReplaySettings }): void {
            this.appReplaySettings[appId] = replaySettings;
        },
        // Live replay enablement is per app, so every replay-enabled app's settings are needed to
        // know whether the subscription can use live replays at all.
        async loadReplaySettingsForReplayEnabledApps (): Promise<void> {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const appsStore = useAppsStore();

            const appIds: string[] = appsStore.list
                .filter(({ id }: { id: string }) => appsStore.getApplicationFlag(id, 'enableSessionReplay') && canViewSessionReplayForApp(id))
                .map(({ id }: { id: string }) => id);

            await Promise.all(appIds.map(async (appId: string) => {
                try {
                    await this.loadReplaySettingsForApp({ appId });
                } catch (error) {
                    console.error(`Unable to fetch replay settings for app ${appId}:`, error);
                }
            }));
        },
        async handleStarChange (replayBody: Record<string, unknown>): Promise<Record<string, unknown>> {
            if (get(replayBody, 'starred', false)) {
                try {
                    await this.starReplay({ replayBody });

                    const replayEntity = this.getReplayEntityAtKey(replayBody?.recordingSessionId as string);

                    if (!replayEntity || !replayEntity.id) {
                        throw new Error();
                    }

                    return { ...replayBody, id: replayEntity.id };
                } catch {
                    return { ...replayBody, starred: false, errorType: 'star' };
                }
            }

            try {
                await this.unstarReplay({ replay: replayBody as SessionReplayEntity });

                return replayBody;
            } catch {
                return { ...replayBody, starred: true, errorType: 'unstar' };
            }
        },
        async handleWatchedReplay (replayBody: Record<string, unknown>): Promise<Record<string, unknown>> {
            try {
                await this.watchedReplay({ replayBody });

                const { id: replayEntityId } = this.getReplayEntityAtKey(replayBody?.recordingSessionId as string) ?? {};

                if (!replayEntityId) {
                    throw new Error();
                }

                return { ...replayBody, id: replayEntityId };
            } catch {
                return { ...replayBody, watched: false };
            }
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSessionReplayStore, module.hot));
}
